/**
 * useProductionTemplate hook for production template generation with real-time progress
 * 
 * This hook manages the complete lifecycle of production template generation:
 * - Starting the production template job
 * - SSE streaming of progress updates (9 stages)
 * - Error handling and retry logic
 * - Auto-reconnect on disconnect
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { getAPIBaseUrl, getAuthHeaders } from '@tracekit/core';

export type ProductionTemplateStage =
  | 'idle'
  | 'queued'
  | 'parsing'
  | 'structure_extraction'
  | 'chunking'
  | 'criteria_extraction'
  | 'criteria_normalization'
  | 'section_mapping'
  | 'staff_requirements_extraction'
  | 'company_requirements_extraction'
  | 'question_generation'
  | 'staff_company_question_generation'
  | 'template_building'
  | 'markdown_rendering'
  | 'complete'
  | 'error';

export interface ProductionTemplateMetadata {
  criteria_count?: number;
  question_count?: number;
  section_count?: number;
  chunk_count?: number;
  quality_score?: number;
  solicitation_constraints_status?: string;
  page_limit?: number | null;
  prohibited_topics_count?: number;
  admin_requirements_count?: number;
  constraints_review_required?: boolean;
  proposal_outline_status?: string;
  proposal_outline_review_required?: boolean;
  proposal_outline_body_budget_words?: number | null;
  outline_slot_count?: number;
  outline_staff_slot_count?: number;
  outline_company_slot_count?: number;
  response_mode_counts?: Record<string, number>;
}

// Enriched Criterion with full details
export interface EnrichedCriterion {
  id: string;
  criterion_id: string;
  type: 'gate' | 'scored';
  kind: 'gate' | 'scored';  // Backward compatibility
  title: string;
  description: string;
  weight: number;
  points: number;
  source_section: string;
  page: number;
  question_count: number;
  questions: any[];
  mapped_sections: Array<{
    title: string;
    page: number;
  }>;
  response_mode?: string;
  satisfied_by?: string;
  output_budget_words?: number;
  prohibited_topics?: string[];
  original_data: any;
}

// RFP Document Section
export interface RfpDocumentSection {
  id: string;
  name: string;
  title: string;
  description: string;
  page: number;
  criteria_ids: string[];
  type: string;
}

// Compliance Matrix
export interface ComplianceMatrix {
  gate_criteria: EnrichedCriterion[];
  scored_criteria: EnrichedCriterion[];
  total_points: number;
  total_criteria: number;
}

// Production Template Result - NEW STRUCTURE
export interface ProductionTemplateResult {
  // PRIMARY: Criteria with full details
  criteria: EnrichedCriterion[];
  
  // SECONDARY: RFP document structure
  rfp_sections: RfpDocumentSection[];
  sections: RfpDocumentSection[];  // Alias for backward compatibility
  
  // COMPLIANCE & APPENDICES
  compliance_matrix: ComplianceMatrix;
  appendices: string[];
  
  // QUESTIONS
  questions: any[];
  
  // TEMPLATE & MARKDOWN
  template: any;
  markdown: string;
  
  // STATISTICS
  solicitation_constraints?: any;
  proposal_outline_plan?: any;
  stats: {
    criteria_count: number;
    gate_criteria_count: number;
    scored_criteria_count: number;
    total_points: number;
    question_count: number;
    chunk_count: number;
    rfp_section_count: number;
    appendix_count: number;
    admin_requirements_count?: number;
    prohibited_topics_count?: number;
    constraints_review_required?: boolean;
    quality_score: number;
    runtime_seconds: number;
    avg_questions_per_criterion: number;
  };
  
  // METADATA
  metadata: {
    generated_at: string;
    opportunity_id: string;
    project_id: string;
    artifact_ids: string[];
    use_llm_validation?: boolean;
    // RFP Information
    solicitation_number?: string;
    client_name?: string;
    due_date?: string;
    jurisdiction?: string;
    contact_person?: string;
    submission_method?: string;
    // Stats (for convenience)
    criteria_count: number;
    question_count: number;
    quality_score: number;
    runtime_seconds: number;
    avg_questions_per_criterion: number;
  };
}

export interface ProductionTemplateState {
  stage: ProductionTemplateStage;
  message: string;
  progress: number; // 0-100
  error: string | null;
  isStreaming: boolean;
  isLoading: boolean;
  elapsed: number; // seconds
  eta: number | null; // estimated seconds remaining
  jobId: string | null;
  result: ProductionTemplateResult | null;
  metadata: ProductionTemplateMetadata;
}

interface StreamCallbacks {
  onProgress: (data: any) => void;
  onComplete: (data: any) => void;
  onError: (error: Error) => void;
}

const initialState: ProductionTemplateState = {
  stage: 'idle',
  message: '',
  progress: 0,
  error: null,
  isStreaming: false,
  isLoading: false,
  elapsed: 0,
  eta: null,
  jobId: null,
  result: null,
  metadata: {},
};

/**
 * Fetch RFP document artifact IDs for an opportunity (when not provided by caller)
 */
async function fetchOpportunityRfpArtifactIds(
  opportunityId: string,
  projectId: string
): Promise<string[]> {
  const baseUrl = getAPIBaseUrl();
  const authHeaders = getAuthHeaders();
  const url = `${baseUrl}/api/opportunities/${opportunityId}/rfp-documents?project_id=${encodeURIComponent(projectId)}`;
  const res = await fetch(url, { method: 'GET', headers: { ...authHeaders } });
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  const docs = data.documents || data.artifacts || [];
  return docs
    .map((d: { artifact_id?: string; id?: string }) => d.artifact_id || d.id)
    .filter(Boolean);
}

/**
 * Start a production template generation job
 */
async function startProductionTemplateJob(
  opportunityId: string,
  projectId: string,
  artifactIds: string[],
  useLLMValidation: boolean = false
): Promise<{ job_id: string; status: string; message: string }> {
  const baseUrl = getAPIBaseUrl();
  const authHeaders = getAuthHeaders();

  // If no artifact IDs passed, try to resolve from opportunity's RFP documents
  let idsToUse = artifactIds && artifactIds.length > 0 ? artifactIds : [];
  if (idsToUse.length === 0) {
    idsToUse = await fetchOpportunityRfpArtifactIds(opportunityId, projectId);
  }

  const response = await fetch(
    `${baseUrl}/api/opportunities/${opportunityId}/production-template/from-rfp?project_id=${encodeURIComponent(projectId)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        artifact_ids: idsToUse,
        use_llm_validation: useLLMValidation,
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: response.statusText }));
    const detail = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail || response.statusText);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Check for existing job (in-progress or recently completed)
 */
async function checkExistingJob(
  opportunityId: string,
  projectId: string
): Promise<{ job_id: string; status: string; progress: number; stage: string; message?: string; elapsed?: number; eta?: number; result?: any } | null> {
  const baseUrl = getAPIBaseUrl();
  const authHeaders = getAuthHeaders();

  try {
    // Fetch job history
    const historyResponse = await fetch(
      `${baseUrl}/api/opportunities/${opportunityId}/production-template/history?project_id=${encodeURIComponent(projectId)}&limit=1`,
      {
        method: 'GET',
        headers: {
          ...authHeaders,
        },
      }
    );

    if (!historyResponse.ok) {
      console.log('[ProductionTemplate] No job history found');
      return null;
    }

    const data = await historyResponse.json();
    const jobs = data.jobs || [];
    
    if (jobs.length === 0) {
      console.log('[ProductionTemplate] No previous jobs found');
      return null;
    }

    const latestJob = jobs[0];
    console.log('[ProductionTemplate] Found latest job:', {
      id: latestJob.id,
      status: latestJob.status,
      created_at: latestJob.created_at,
    });
    
    // For any non-failed/non-cancelled job, fetch full status
    if (latestJob.status !== 'failed' && latestJob.status !== 'cancelled') {
      console.log('[ProductionTemplate] Fetching full status for job:', latestJob.id);
      
      // Fetch full job status with result
      const statusResponse = await fetch(
        `${baseUrl}/api/opportunities/${opportunityId}/production-template/status/${latestJob.id}?project_id=${encodeURIComponent(projectId)}`,
        {
          method: 'GET',
          headers: {
            ...authHeaders,
          },
        }
      );

      if (!statusResponse.ok) {
        console.warn('[ProductionTemplate] Failed to fetch job status, using history data');
        return null;
      }

      const jobStatus = await statusResponse.json();
      console.log('[ProductionTemplate] Job status fetched:', {
        status: jobStatus.status,
        progress: jobStatus.progress,
        stage: jobStatus.stage,
        has_result: !!jobStatus.result,
      });

      return {
        job_id: latestJob.id,
        status: jobStatus.status,
        progress: jobStatus.progress || 0,
        stage: jobStatus.stage || 'queued',
        message: jobStatus.message || '',
        elapsed: jobStatus.elapsed || 0,
        eta: jobStatus.eta,
        result: jobStatus.result, // Include the full result for completed jobs
      };
    }

    // Job is failed or cancelled - don't return it
    console.log('[ProductionTemplate] Latest job is in terminal state:', latestJob.status);
    return null;
  } catch (error) {
    console.error('[ProductionTemplate] Failed to check existing job:', error);
    return null;
  }
}

/**
 * Stream production template progress via SSE
 */
async function streamProductionTemplateProgress(
  opportunityId: string,
  jobId: string,
  projectId: string,
  callbacks: StreamCallbacks
): Promise<() => void> {
  const baseUrl = getAPIBaseUrl();
  const authHeaders = getAuthHeaders();

  let aborted = false;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  const abort = () => {
    aborted = true;
    if (reader) {
      reader.cancel();
      reader = null;
    }
  };

  // Start streaming in background
  (async () => {
    try {
      const url = `${baseUrl}/api/opportunities/${opportunityId}/production-template/stream?job_id=${jobId}&project_id=${encodeURIComponent(projectId)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          ...authHeaders,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to connect to stream: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!aborted) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('[ProductionTemplate] Stream ended');
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete message in buffer

        for (const line of lines) {
          if (!line.trim()) continue;

          // Parse SSE format: "event: <type>\ndata: <json>"
          const eventMatch = line.match(/event:\s*(\w+)/);
          const dataMatch = line.match(/data:\s*({.*})/s);

          if (!dataMatch) continue;

          try {
            const data = JSON.parse(dataMatch[1]);
            const eventType = eventMatch ? eventMatch[1] : 'progress';

            console.log(`[ProductionTemplate] Event: ${eventType}`, data);

            if (eventType === 'progress') {
              callbacks.onProgress(data);
            } else if (eventType === 'complete') {
              callbacks.onComplete(data);
              abort(); // Stop streaming on complete
              break;
            } else if (eventType === 'error') {
              callbacks.onError(new Error(data.message || 'Production template generation failed'));
              abort();
              break;
            }
          } catch (e) {
            console.error('[ProductionTemplate] Failed to parse SSE data:', e, line);
          }
        }
      }
    } catch (error: any) {
      if (!aborted) {
        console.error('[ProductionTemplate] Stream error:', error);
        callbacks.onError(error);
      }
    } finally {
      if (reader) {
        reader.cancel();
        reader = null;
      }
    }
  })();

  return abort;
}

/**
 * Hook for production template generation with real-time progress monitoring
 */
export function useProductionTemplate() {
  const [state, setState] = useState<ProductionTemplateState>(initialState);
  const abortRef = useRef<(() => void) | null>(null);
  const isStartingRef = useRef(false);
  /** Stable job id for stream retry (avoid stale closure from useCallback deps). */
  const jobIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number>(0);
  const elapsedIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef<number>(0);
  const maxRetries = 3;

  // Update elapsed time during streaming
  useEffect(() => {
    if (!state.isStreaming) {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
      return;
    }

    elapsedIntervalRef.current = setInterval(() => {
      if (startTimeRef.current > 0) {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        setState((prev) => ({
          ...prev,
          elapsed: Math.round(elapsed * 10) / 10, // Round to 1 decimal
        }));
      }
    }, 100); // Update every 100ms for smooth elapsed time

    return () => {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
    };
  }, [state.isStreaming]);

  /**
   * Start production template generation
   */
  const startGeneration = useCallback(
    async (
      opportunityId: string,
      projectId: string,
      artifactIds: string[],
      useLLMValidation: boolean = false
    ) => {
      // Cancel any existing job
      if (abortRef.current) {
        abortRef.current();
        abortRef.current = null;
      }

      // Prevent duplicate concurrent requests
      if (isStartingRef.current) {
        return;
      }

      isStartingRef.current = true;
      startTimeRef.current = Date.now();
      retryCountRef.current = 0;

      // Reset state and start loading
      setState({
        ...initialState,
        isLoading: true,
        stage: 'queued',
        message: 'Starting production template generation...',
      });

      try {
        // Step 1: Start the job
        const jobResponse = await startProductionTemplateJob(
          opportunityId,
          projectId,
          artifactIds,
          useLLMValidation
        );

        console.log('[ProductionTemplate] Job started:', jobResponse);

        jobIdRef.current = jobResponse.job_id;

        setState((prev) => ({
          ...prev,
          isLoading: false,
          isStreaming: true,
          jobId: jobResponse.job_id,
          message: jobResponse.message || 'Job started, monitoring progress...',
        }));

        // Step 2: Start streaming progress
        const jobIdForFetch = jobResponse.job_id;
        const callbacks: StreamCallbacks = {
          onProgress: (data) => {
            console.log('[ProductionTemplate] Progress update:', data);
            const isComplete = data.stage === 'complete' || data.progress >= 100;
            setState((prev) => ({
              ...prev,
              stage: (data.stage as ProductionTemplateStage) || prev.stage,
              message: data.message || prev.message,
              progress: Math.max(0, Math.min(100, data.progress ?? prev.progress)),
              elapsed: data.elapsed ?? prev.elapsed,
              eta: data.eta ?? prev.eta,
              metadata: { ...prev.metadata, ...(data.metadata || {}) },
              ...(data.result ? { result: data.result } : {}),
              ...(isComplete ? { isStreaming: false } : {}),
            }));
            // When stream sends stage=complete without result (e.g. only event: progress), fetch result so TemplatePreview shows and auto-accept can run
            if (isComplete && !data.result) {
              abortRef.current = null;
              isStartingRef.current = false;
              if (elapsedIntervalRef.current) {
                clearInterval(elapsedIntervalRef.current);
                elapsedIntervalRef.current = null;
              }
              (async () => {
                const baseUrl = getAPIBaseUrl();
                const authHeaders = getAuthHeaders();
                const url = `${baseUrl}/api/opportunities/${opportunityId}/production-template/status/${jobIdForFetch}?project_id=${encodeURIComponent(projectId)}`;
                for (let attempt = 0; attempt < 5; attempt++) {
                  try {
                    const res = await fetch(url, { method: 'GET', headers: { ...authHeaders } });
                    if (!res.ok) continue;
                    const jobStatus = await res.json();
                    if (jobStatus?.result) {
                      setState((prev) => ({ ...prev, result: jobStatus.result }));
                      return;
                    }
                  } catch (err) {
                    console.warn('[ProductionTemplate] Fetch result attempt', attempt + 1, err);
                  }
                  await new Promise((r) => setTimeout(r, 1500));
                }
              })();
            }
          },

          onComplete: (data) => {
            console.log('[ProductionTemplate] Complete:', data);
            setState((prev) => ({
              ...prev,
              stage: 'complete',
              message: data.message || 'Production template generated successfully!',
              progress: 100,
              isStreaming: false,
              result: data.result || null,
              elapsed: data.elapsed ?? prev.elapsed,
              metadata: data.result?.metadata ? { ...prev.metadata, ...data.result.metadata } : prev.metadata,
            }));
            abortRef.current = null;
            isStartingRef.current = false;
            if (elapsedIntervalRef.current) {
              clearInterval(elapsedIntervalRef.current);
              elapsedIntervalRef.current = null;
            }
          },

          onError: (error) => {
            console.error('[ProductionTemplate] Error:', error);
            
            // Don't retry on storage/file errors - these won't resolve with retries
            const isStorageError = error.message && (
              error.message.includes('Storage path not found') ||
              error.message.includes('File not found') ||
              error.message.includes('RFP document file not found') ||
              error.message.includes('re-upload')
            );
            
            if (isStorageError) {
              // Storage errors are permanent - don't retry
              setState((prev) => ({
                ...prev,
                stage: 'error',
                error: error.message,
                message: error.message || 'RFP document file not found. Please re-upload the document.',
                isStreaming: false,
                isLoading: false,
              }));
              abortRef.current = null;
              isStartingRef.current = false;
              if (elapsedIntervalRef.current) {
                clearInterval(elapsedIntervalRef.current);
                elapsedIntervalRef.current = null;
              }
              return;
            }
            
            // Retry logic for transient errors only
            if (retryCountRef.current < maxRetries) {
              retryCountRef.current++;
              console.log(`[ProductionTemplate] Retrying (${retryCountRef.current}/${maxRetries})...`);
              
              setState((prev) => ({
                ...prev,
                message: `Connection lost, retrying (${retryCountRef.current}/${maxRetries})...`,
              }));

              // Retry after 2 seconds
              setTimeout(() => {
                const jid = jobIdRef.current;
                if (jid && !abortRef.current) {
                  streamProductionTemplateProgress(opportunityId, jid, projectId, callbacks)
                    .then((abort) => {
                      abortRef.current = abort;
                    });
                }
              }, 2000);
            } else {
              setState((prev) => ({
                ...prev,
                stage: 'error',
                error: error.message,
                message: `Error: ${error.message}`,
                isStreaming: false,
                isLoading: false,
              }));
              abortRef.current = null;
              isStartingRef.current = false;
              if (elapsedIntervalRef.current) {
                clearInterval(elapsedIntervalRef.current);
                elapsedIntervalRef.current = null;
              }
            }
          },
        };

        const abort = await streamProductionTemplateProgress(
          opportunityId,
          jobResponse.job_id,
          projectId,
          callbacks
        );
        abortRef.current = abort;
        // Keep isStartingRef true until onComplete / onError / cancel / reset so we never POST from-rfp twice in parallel.
      } catch (error: any) {
        console.error('[ProductionTemplate] Failed to start:', error);
        setState((prev) => ({
          ...prev,
          stage: 'error',
          error: error.message,
          message: `Failed to start generation: ${error.message}`,
          isStreaming: false,
          isLoading: false,
        }));
        jobIdRef.current = null;
        isStartingRef.current = false;
      }
    },
    []
  );

  /**
   * Cancel production template generation
   */
  const cancelGeneration = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }
    jobIdRef.current = null;
    isStartingRef.current = false;
    retryCountRef.current = 0;
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      isStreaming: false,
      isLoading: false,
      stage: 'idle',
      message: 'Generation cancelled',
      jobId: null,
    }));
  }, []);

  /**
   * Tell the API to cancel the Celery job, then stop the local SSE client.
   * Captures job id before clearing refs.
   */
  const cancelJobRemotelyAndStop = useCallback(
    async (opportunityId: string, projectId: string) => {
      const jid = jobIdRef.current;
      if (jid) {
        try {
          const baseUrl = getAPIBaseUrl();
          const authHeaders = getAuthHeaders();
          const url = `${baseUrl}/api/opportunities/${opportunityId}/production-template/jobs/${jid}/cancel?project_id=${encodeURIComponent(projectId)}`;
          const res = await fetch(url, {
            method: 'POST',
            headers: { ...authHeaders },
          });
          if (!res.ok) {
            const text = await res.text();
            console.warn('[ProductionTemplate] Server cancel failed:', res.status, text);
          } else {
            console.log('[ProductionTemplate] Job cancelled on server:', jid);
          }
        } catch (e) {
          console.warn('[ProductionTemplate] Server cancel request error:', e);
        }
      } else {
        console.warn('[ProductionTemplate] cancelJobRemotelyAndStop: no job id in ref; stopping local stream only');
      }
      cancelGeneration();
    },
    [cancelGeneration]
  );

  /**
   * Resume an existing job
   */
  const resumeJob = useCallback(
    async (
      opportunityId: string,
      projectId: string,
      jobId: string,
      currentProgress: number = 0,
      currentStage: ProductionTemplateStage = 'queued'
    ) => {
      // Cancel any existing stream
      if (abortRef.current) {
        abortRef.current();
        abortRef.current = null;
      }

      isStartingRef.current = true;
      startTimeRef.current = Date.now();
      retryCountRef.current = 0;
      jobIdRef.current = jobId;

      setState({
        ...initialState,
        isStreaming: true,
        jobId,
        stage: currentStage,
        progress: currentProgress,
        message: 'Resuming job...',
      });

      try {
        const jobIdForResume = jobId;
        const callbacks: StreamCallbacks = {
          onProgress: (data) => {
            console.log('[ProductionTemplate] Progress update:', data);
            const isComplete = data.stage === 'complete' || data.progress >= 100;
            setState((prev) => ({
              ...prev,
              stage: (data.stage as ProductionTemplateStage) || prev.stage,
              message: data.message || prev.message,
              progress: Math.max(0, Math.min(100, data.progress ?? prev.progress)),
              elapsed: data.elapsed ?? prev.elapsed,
              eta: data.eta ?? prev.eta,
              metadata: { ...prev.metadata, ...(data.metadata || {}) },
              ...(data.result ? { result: data.result } : {}),
              ...(isComplete ? { isStreaming: false } : {}),
            }));
            if (isComplete && !data.result) {
              abortRef.current = null;
              isStartingRef.current = false;
              if (elapsedIntervalRef.current) {
                clearInterval(elapsedIntervalRef.current);
                elapsedIntervalRef.current = null;
              }
              (async () => {
                const baseUrl = getAPIBaseUrl();
                const authHeaders = getAuthHeaders();
                const url = `${baseUrl}/api/opportunities/${opportunityId}/production-template/status/${jobIdForResume}?project_id=${encodeURIComponent(projectId)}`;
                for (let attempt = 0; attempt < 5; attempt++) {
                  try {
                    const res = await fetch(url, { method: 'GET', headers: { ...authHeaders } });
                    if (!res.ok) continue;
                    const jobStatus = await res.json();
                    if (jobStatus?.result) {
                      setState((prev) => ({ ...prev, result: jobStatus.result }));
                      return;
                    }
                  } catch (err) {
                    console.warn('[ProductionTemplate] Fetch result attempt', attempt + 1, err);
                  }
                  await new Promise((r) => setTimeout(r, 1500));
                }
              })();
            }
          },

          onComplete: (data) => {
            console.log('[ProductionTemplate] Complete:', data);
            setState((prev) => ({
              ...prev,
              stage: 'complete',
              message: data.message || 'Production template generated successfully!',
              progress: 100,
              isStreaming: false,
              result: data.result || null,
              elapsed: data.elapsed ?? prev.elapsed,
              metadata: data.result?.metadata ? { ...prev.metadata, ...data.result.metadata } : prev.metadata,
            }));
            abortRef.current = null;
            isStartingRef.current = false;
            if (elapsedIntervalRef.current) {
              clearInterval(elapsedIntervalRef.current);
              elapsedIntervalRef.current = null;
            }
          },

          onError: (error) => {
            console.error('[ProductionTemplate] Error:', error);
            setState((prev) => ({
              ...prev,
              stage: 'error',
              error: error.message,
              message: `Error: ${error.message}`,
              isStreaming: false,
              isLoading: false,
            }));
            abortRef.current = null;
            isStartingRef.current = false;
            if (elapsedIntervalRef.current) {
              clearInterval(elapsedIntervalRef.current);
              elapsedIntervalRef.current = null;
            }
          },
        };

        const abort = await streamProductionTemplateProgress(
          opportunityId,
          jobId,
          projectId,
          callbacks
        );
        abortRef.current = abort;
        // Keep isStartingRef true until terminal stream events (same as startGeneration).
      } catch (error: any) {
        console.error('[ProductionTemplate] Failed to resume:', error);
        setState((prev) => ({
          ...prev,
          stage: 'error',
          error: error.message,
          message: `Failed to resume job: ${error.message}`,
          isStreaming: false,
          isLoading: false,
        }));
        jobIdRef.current = null;
        isStartingRef.current = false;
      }
    },
    []
  );

  /**
   * Check for existing job on mount
   */
  const checkForExistingJob = useCallback(
    async (opportunityId: string, projectId: string) => {
      const existingJob = await checkExistingJob(opportunityId, projectId);
      return existingJob;
    },
    []
  );

  /**
   * Load a completed job result directly (without streaming)
   */
  const loadCompletedResult = useCallback(
    (jobId: string, result: any, elapsed: number = 0) => {
      console.log('[ProductionTemplate] Loading completed result:', { jobId, hasResult: !!result });
      setState({
        ...initialState,
        jobId,
        stage: 'complete',
        progress: 100,
        message: 'Production template generated successfully!',
        result,
        elapsed,
        isStreaming: false,
        isLoading: false,
      });
    },
    []
  );

  /**
   * Reset hook to initial state
   */
  const reset = useCallback(() => {
    cancelGeneration();
    jobIdRef.current = null;
    isStartingRef.current = false;
    startTimeRef.current = 0;
    retryCountRef.current = 0;
    setState(initialState);
  }, [cancelGeneration]);

  return {
    ...state,
    startGeneration,
    cancelGeneration,
    cancelJobRemotelyAndStop,
    resumeJob,
    checkForExistingJob,
    loadCompletedResult,
    reset,
  };
}
